import { type ColumnDef, type SortingState } from '@tanstack/react-table'

import { type FunctionComponent, useState } from 'react'

import type { Webhook } from 'src/types/webhook'

import useDeleteWebhookMutation from '~/hooks/services/webhooks/mutations/useDeleteWebhookMutation'

import { PiTrash } from 'react-icons/pi'

import Table from '../Common/Table/Table'
import ConfirmationModal from '../Modals/ConfirmationModal'

interface Props {
  webhooks: Webhook[]
  onEditWebhook?: (webhook: Webhook) => void
}

const WebhookTable: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { webhooks, onEditWebhook } = props

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean
    webhook: Webhook | null
  }>({ isOpen: false, webhook: null })
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { mutateAsync: deleteWebhook } = useDeleteWebhookMutation()

  const columns: ColumnDef<Webhook>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ getValue }) => <span>{(getValue() as string) || '-'}</span>
    },
    {
      header: 'URL',
      accessorKey: 'url',
      cell: ({ getValue }) => <span>{(getValue() as string) || '-'}</span>
    },
    {
      header: 'Auth Type',
      accessorKey: 'authType',
      cell: ({ getValue }) => <span>{getValue() as string}</span>
    },
    {
      header: 'Events',
      accessorKey: 'events',
      cell: ({ getValue }) => {
        const events = getValue() as string[]
        return <span>{events?.length ? events.join(', ') : '-'}</span>
      }
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-center">
          <button
            className="text-th-error transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteModalState({ isOpen: true, webhook: row.original })
            }}
            title="Delete webhook"
            disabled={deletingId === row.original.id}
          >
            {deletingId === row.original.id ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-th-text-secondary border-t-transparent" />
            ) : (
              <PiTrash className="h-4 w-4" />
            )}
          </button>
        </div>
      )
    }
  ]

  const defaultSorting: SortingState = [
    {
      id: 'name',
      desc: false
    }
  ]

  const handleRowClick = (webhook: Webhook) => {
    if (deletingId === webhook.id) return
    if (deleteModalState.isOpen && deleteModalState.webhook?.id === webhook.id) return
    onEditWebhook?.(webhook)
  }

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false, webhook: null })
  }

  const confirmDelete = async () => {
    if (deleteModalState.webhook) {
      const id = deleteModalState.webhook.id
      closeDeleteModal()
      setDeletingId(id)
      try {
        await deleteWebhook(id)
      } finally {
        setDeletingId(null)
      }
    }
  }

  const DeleteConfirmationModal = (
    <ConfirmationModal
      isOpen={deleteModalState.isOpen}
      onClose={closeDeleteModal}
      onConfirm={confirmDelete}
      title="Delete Webhook"
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
    >
      <div className="space-y-4">
        <p>
          Are you sure you want to delete the webhook <strong>"{deleteModalState.webhook?.name || 'Unnamed'}"</strong>?
        </p>

        <div className="bg-th-warning/10 rounded-lg border border-th-warning p-3 text-sm">
          <p className="mb-2 font-bold text-th-warning">⚠️ Warning</p>
          <p className="text-th-warning">
            Deleting this webhook will stop all future updates from being delivered to the endpoint. Any systems relying on this webhook will no longer receive
            notifications.
          </p>
        </div>

        <div className="rounded-lg bg-th-content-secondary p-3 text-sm">
          <p className="mb-1 font-semibold">Endpoint URL:</p>
          <p className="font-mono break-all text-th-text-secondary">{deleteModalState.webhook?.url}</p>
        </div>

        {deleteModalState.webhook?.events && deleteModalState.webhook.events.length > 0 && (
          <div className="rounded-lg bg-th-content-secondary p-3 text-sm">
            <p className="mb-1 font-semibold">Events that will stop being delivered:</p>
            <ul className="list-inside list-disc space-y-1">
              {deleteModalState.webhook.events.map((event, index) => (
                <li key={index} className="text-th-text-secondary">
                  {event}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ConfirmationModal>
  )

  return (
    <>
      <Table columns={columns} data={webhooks} defaultSortBy={defaultSorting} onRowClick={handleRowClick} />
      {DeleteConfirmationModal}
    </>
  )
}

export default WebhookTable
